import { Input } from '@shared/components/common/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/common/Select';
import { UserPublication } from '@shared/types/profile';
import { TYPE_SEARCH, TypeSearch } from '@shared/types/enums';
import { Search } from 'lucide-react';
import { useState } from 'react';
import PublicationCard from './PublicationCard';
import { useStore } from '@shared/hooks/useStore';

const SEARCH_TYPE_OPTIONS: { label: string, value: TypeSearch }[] = [
    { label: 'OR検索', value: TYPE_SEARCH.OR },
    { label: 'AND検索', value: TYPE_SEARCH.AND }
];

type Props = {
    publications?: UserPublication[],
    isLoading?: boolean,
    onToggleFavoriteSuccess?: () => void,
    onSearchChange?: (search: string, typeSearch: TypeSearch) => void,
    type?: 'default' | 'history'
};

const PublicationContainer = ({
    publications = [],
    isLoading = false,
    onToggleFavoriteSuccess,
    onSearchChange,
    type = 'default' }: Props) => {
    const [searchKeyword, setSearchKeyword] = useState('');
    const [searchType, setSearchType] = useState<TypeSearch>(TYPE_SEARCH.OR);
    const { readerStore } = useStore();

    const handleSearchChange = (value: string) => {
        setSearchKeyword(value);
        onSearchChange?.(value.trim(), searchType);
    };

    const handleSearchTypeChange = (value: TypeSearch) => {
        setSearchType(value);
        onSearchChange?.(searchKeyword.trim(), value);
    };

    const handleSearchBlur = () => {
        const trimmedValue = searchKeyword.trim();
        if (trimmedValue !== searchKeyword) {
            setSearchKeyword(trimmedValue);
            onSearchChange?.(trimmedValue, searchType);
        }
    };

    const handleReadPublicationWithKeyword = (publication: UserPublication) => {
        const url = new URL(`/reader/${publication.id}`, window.location.origin);

        const newSearchKeyword = searchKeyword.trim();
        if (newSearchKeyword && publication.searchCount !== undefined) {
            url.searchParams.set('search', newSearchKeyword);
            url.searchParams.set('searchType', searchType);
        }
        readerStore.recordRecentlyViewed(publication.id);
        readerStore.recordReading(publication.id);
        window.open(url.toString(), 'jiho_reader');
    };

    return (
        <section className='space-y-6 w-full'>
            {/* Search Bar */}
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4'>
                <div className='flex flex-1 max-w-md items-center rounded-full border border-input bg-white focus-within:border-primary transition-colors'>
                    <Select value={searchType} onValueChange={handleSearchTypeChange}>
                        <SelectTrigger className='w-[120px] sm:w-[130px] rounded-l-full rounded-r-none border-0 min-h-12 bg-transparent text-sm shrink-0 focus:ring-0 focus:ring-offset-0'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SEARCH_TYPE_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className='w-px self-stretch bg-input mx-1' />
                    <div className='relative flex-1'>
                        <Input
                            type='text'
                            placeholder='キーワードを入力して検索'
                            className='pr-12 pl-3 rounded-r-full rounded-l-none min-h-12 text-base border-0 bg-transparent focus-visible:border-0 shadow-none overflow-hidden text-ellipsis'
                            value={searchKeyword}
                            onChange={e => handleSearchChange(e.target.value)}
                            onBlur={handleSearchBlur}
                        />
                        <Search className='absolute right-4 top-1/2 -translate-y-1/2 size-5 text-[#999] pointer-events-none' />
                    </div>
                </div>
            </div>

            {/* Publications Grid */}
            {isLoading && publications.length === 0 ?
                (
                    <div className='text-center py-12 text-gray-500'>
                        <p>読み込み中...</p>
                    </div>
                ) :
                publications.length > 0 ?
                    (
                        <ul className='grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch'>
                            {publications.map(publication => (
                                <li key={publication.id} className='flex animate-in fade-in zoom-in-90 duration-300'>
                                    <PublicationCard
                                        publication={publication}
                                        onToggleFavoriteSuccess={onToggleFavoriteSuccess}
                                        type={type}
                                        onReadPublicationWithKeyword={handleReadPublicationWithKeyword}
                                    />
                                </li>
                            ))}
                        </ul>
                    ) :
                    (
                        <div className='text-center py-12 text-gray-500'>
                            <p>
                                {searchKeyword ?
                                    '検索結果が見つかりませんでした。' :
                                    '出版物がありません。'}
                            </p>
                        </div>
                    )}
        </section>
    );
};

export default PublicationContainer;
